import React from "react";

import { styled } from "@compiled/react";
import { useIntl } from "react-intl";
import { Provider } from "react-redux";

import "@atlaskit/tokens/figma/atlassian-light.json";
import "@atlaskit/tokens/figma/atlassian-dark.json";
import {
    Content,
    Main,
    PageLayout,
    TopNavigation
} from "@atlaskit/page-layout";
import { Box } from "@atlaskit/primitives";
import Spinner from "@atlaskit/spinner";

import {
    FastshiftDashboard,
    FastshiftDashboardV2,
    FastshiftNavigation
} from "@/components/fastshift/src";
import { store as FastShiftPortalStore } from "@/components/fastshift/src/state/store";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";
import { FourOhFourPageTemplate } from "@/templates/404/404-page/src/404-page.template";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { messages } from "../messages";

enum ValidationState {
    CHECKING_TOKEN = "Loading",
    FASTSHIFT_ENABLED_USER = "FastshiftEnabledUser",
    NON_FASTSHIFT_USER = "NonFastshiftUser"
}

const BASE_URL =
    process.env["BASE_URL"] || process.env["NODE_ENV"] === "production"
        ? "https://www.atlassian.com"
        : "https://wac.stg.internal.atlassian.com"; // fallback for local development

const PageContainer = styled.span({
    height: "100vh",
    width: "100vw",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
});

const AuthenticatedFastshiftDashboardPage: React.FC = () => {
    const { formatMessage } = useIntl();

    const isMilestone2Enabled = checkFeatureFlagGate(
        featureFlags.sxpFastshiftPortalMilestone2Gate
    );

    return (
        <Provider store={FastShiftPortalStore}>
            <style
                dangerouslySetInnerHTML={{
                    __html: `.grecaptcha-badge { visibility: hidden !important; }`
                }}
            />
            <PageLayout>
                <TopNavigation
                    testId="topNavigation"
                    id="product-navigation"
                    skipLinkTitle={formatMessage(messages.pageSkipLinkTitle)}
                    height={60}
                    isFixed
                >
                    <FastshiftNavigation />
                </TopNavigation>
                <Content testId="content">
                    <Main testId="main" id="main" skipLinkTitle="Main Content">
                        <Box padding="space.200">
                            {isMilestone2Enabled ? (
                                <FastshiftDashboardV2 />
                            ) : (
                                <FastshiftDashboard />
                            )}
                        </Box>
                    </Main>
                </Content>
            </PageLayout>
        </Provider>
    );
};

const FastshiftDashboardPageTemplateV1DashboardPage: React.FC = () => {
    const stargateClient = useBxpWebStargateClient();
    const [validationState, setValidationState] =
        React.useState<ValidationState>(ValidationState.CHECKING_TOKEN);
    const isFastshiftEnabledUser = checkFeatureFlagGate(
        featureFlags.sxpEnableFastshiftPortal
    );

    React.useEffect(() => {
        const getUserProfile = async () => {
            return await stargateClient.me({});
        };

        getUserProfile()
            .then(() => {
                // check if user is whitelisted for fastshift portal
                if (isFastshiftEnabledUser) {
                    setValidationState(ValidationState.FASTSHIFT_ENABLED_USER);
                } else {
                    setValidationState(ValidationState.NON_FASTSHIFT_USER);
                }
            })
            .catch(() => {
                // since token is invalid, redirect to login page
                const loginUrl = `${process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"]}/login?continue=${BASE_URL}/fastshift/dashboard`;
                window.location.href = loginUrl;
            });
    }, [stargateClient, isFastshiftEnabledUser]);

    switch (validationState) {
        case ValidationState.CHECKING_TOKEN:
            return (
                <PageContainer>
                    <Spinner
                        size="xlarge"
                        label="Loading"
                        testId="loading-spinner"
                    />
                </PageContainer>
            );
        case ValidationState.FASTSHIFT_ENABLED_USER:
            return <AuthenticatedFastshiftDashboardPage />;
        case ValidationState.NON_FASTSHIFT_USER:
        default:
            return <FourOhFourPageTemplate />;
            break;
    }
};

export const FastshiftDashboardPageTemplateV1: React.FC = () => {
    // check user is on the correct route - V1 release has only one page
    if (window.location.pathname !== "/fastshift/dashboard") {
        return <FourOhFourPageTemplate />;
    }

    return <FastshiftDashboardPageTemplateV1DashboardPage />;
};
